import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';

export type MonitoringFilterScope = 'global' | 'computer' | 'employee';

export interface MonitoringFilterState {
  date: string;
  employeeId: string;
  deviceId: string;
  campaignId: string;
  eventType: string;
  application: string;
  domain: string;
  browser: string;
  status: string;
  hasSnapshot: string;
  /** Client-side Web Activity evidence filter (duration_basis). */
  evidence: string;
}

export const SESSION_STATUS_OPTIONS = [
  'All',
  'NOT CLOCKED IN',
  'WORKING',
  'BREAK',
  'BATHROOM',
  'LUNCH',
  'IDLE / LOCKED',
  'REAUTH REQUIRED',
  'EMERGENCY ACCESS',
  'CLOSED',
  'CLOCKED OUT',
];

export function defaultMonitoringFilters(overrides?: Partial<MonitoringFilterState>): MonitoringFilterState {
  return {
    date: moment().tz('America/Chicago').format('YYYY-MM-DD'),
    employeeId: '',
    deviceId: '',
    campaignId: '',
    eventType: '',
    application: '',
    domain: '',
    browser: '',
    status: '',
    hasSnapshot: '',
    evidence: '',
    ...overrides,
  };
}

interface MonitoringFiltersProps {
  scope: MonitoringFilterScope;
  value: MonitoringFilterState;
  onChange: (next: MonitoringFilterState) => void;
  lockedDeviceId?: string;
  lockedEmployeeId?: string;
  showEventType?: boolean;
  showApplication?: boolean;
  showDomain?: boolean;
  showBrowser?: boolean;
  showStatus?: boolean;
  showHasSnapshot?: boolean;
  showEvidence?: boolean;
}

const MonitoringFilters: React.FC<MonitoringFiltersProps> = ({
  scope,
  value,
  onChange,
  lockedDeviceId,
  lockedEmployeeId,
  showEventType = false,
  showApplication = false,
  showDomain = false,
  showBrowser = false,
  showStatus = false,
  showHasSnapshot = false,
  showEvidence = false,
}) => {
  const showComputer = scope === 'global';
  const showEmployee = scope !== 'employee';

  const { data: usersData } = useQuery({
    queryKey: ['monitoringFilterUsers'],
    queryFn: () => apiClient.users.getAll(),
    staleTime: 60_000,
  });

  const { data: campaignsData } = useQuery({
    queryKey: ['monitoringFilterCampaigns'],
    queryFn: () => apiClient.campaigns.getAll(),
    staleTime: 60_000,
  });

  const { data: devicesData } = useQuery({
    queryKey: ['monitoringFilterDevices'],
    queryFn: () => apiClient.monitoring.listDevices(),
    staleTime: 60_000,
    enabled: showComputer,
  });

  const users = usersData?.data?.data || [];
  const campaigns = campaignsData?.data?.data || [];
  const devices = devicesData?.data?.data || [];

  const patch = (partial: Partial<MonitoringFilterState>) => {
    onChange({ ...value, ...partial });
  };

  const clear = () => {
    onChange(defaultMonitoringFilters({
      deviceId: scope === 'computer' ? (lockedDeviceId || value.deviceId) : '',
      employeeId: scope === 'employee' ? (lockedEmployeeId || value.employeeId) : '',
    }));
  };

  return (
    <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
      <TextField
        size="small"
        label="Date"
        type="date"
        value={value.date}
        onChange={(e) => patch({ date: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
      {showEmployee && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            label="Employee"
            value={value.employeeId}
            onChange={(e) => patch({ employeeId: String(e.target.value) })}
          >
            <MenuItem value="">All</MenuItem>
            {users.map((user: any) => (
              <MenuItem key={user.id} value={user.id}>
                {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {showComputer && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Computer</InputLabel>
          <Select
            label="Computer"
            value={value.deviceId}
            onChange={(e) => patch({ deviceId: String(e.target.value) })}
          >
            <MenuItem value="">All</MenuItem>
            {devices.map((device: any) => (
              <MenuItem key={device.id} value={device.id}>
                {device.device_name || device.computerName || device.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Campaign</InputLabel>
        <Select
          label="Campaign"
          value={value.campaignId}
          onChange={(e) => patch({ campaignId: String(e.target.value) })}
        >
          <MenuItem value="">All</MenuItem>
          {campaigns.map((campaign: any) => (
            <MenuItem key={campaign.id} value={campaign.id}>
              {campaign.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showEventType && (
        <TextField
          size="small"
          label="Event Type"
          value={value.eventType}
          onChange={(e) => patch({ eventType: e.target.value })}
          placeholder="All"
        />
      )}
      {showApplication && (
        <TextField
          size="small"
          label="Application"
          value={value.application}
          onChange={(e) => patch({ application: e.target.value })}
          placeholder="All"
        />
      )}
      {showDomain && (
        <TextField
          size="small"
          label="Domain"
          value={value.domain}
          onChange={(e) => patch({ domain: e.target.value })}
          placeholder="All"
        />
      )}
      {showBrowser && (
        <TextField
          size="small"
          label="Browser"
          value={value.browser}
          onChange={(e) => patch({ browser: e.target.value })}
          placeholder="All"
        />
      )}
      {showEvidence && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Evidence</InputLabel>
          <Select
            label="Evidence"
            value={value.evidence || ''}
            onChange={(e) => patch({ evidence: String(e.target.value) })}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="FOREGROUND_VERIFIED">Foreground verified</MenuItem>
            <MenuItem value="LEGACY_BROWSER_INTERVAL">Legacy estimate</MenuItem>
            <MenuItem value="TAB_LIFECYCLE_ONLY">Tab activity only</MenuItem>
            <MenuItem value="NONE">No duration evidence</MenuItem>
          </Select>
        </FormControl>
      )}
      {showHasSnapshot && (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Evidence</InputLabel>
          <Select
            label="Evidence"
            value={value.hasSnapshot}
            onChange={(e) => patch({ hasSnapshot: String(e.target.value) })}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="yes">With snapshot</MenuItem>
            <MenuItem value="no">Without snapshot</MenuItem>
          </Select>
        </FormControl>
      )}
      {showStatus && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Session Status</InputLabel>
          <Select
            label="Session Status"
            value={value.status || ''}
            onChange={(e) => patch({ status: String(e.target.value) })}
          >
            {SESSION_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status === 'All' ? '' : status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Button size="small" variant="outlined" onClick={clear}>
        Clear Filters
      </Button>
    </Box>
  );
};

/** Sync filter state with URL query params (preserves unrelated keys like tab). */
export function useMonitoringFilterQuery(
  scope: MonitoringFilterScope,
  lockedDeviceId?: string,
  lockedEmployeeId?: string,
): [MonitoringFilterState, (next: MonitoringFilterState) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const initial = useMemo(() => defaultMonitoringFilters({
    date: searchParams.get('date') || moment().tz('America/Chicago').format('YYYY-MM-DD'),
    employeeId: lockedEmployeeId || searchParams.get('employee_id') || '',
    deviceId: lockedDeviceId || searchParams.get('device_id') || searchParams.get('computer_id') || '',
    campaignId: searchParams.get('campaign_id') || '',
    eventType: searchParams.get('event_type') || '',
    application: searchParams.get('application') || '',
    domain: searchParams.get('domain') || '',
    browser: searchParams.get('browser') || '',
    status: searchParams.get('status') || '',
    hasSnapshot: searchParams.get('has_snapshot') || '',
    evidence: searchParams.get('duration_basis') || '',
  // Intentionally seed once from the landing URL.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lockedDeviceId, lockedEmployeeId]);

  const [value, setValue] = useState<MonitoringFilterState>(initial);

  useEffect(() => {
    if (lockedDeviceId && value.deviceId !== lockedDeviceId) {
      setValue((prev) => ({ ...prev, deviceId: lockedDeviceId }));
    }
  }, [lockedDeviceId, value.deviceId]);

  const update = (next: MonitoringFilterState) => {
    const scoped = {
      ...next,
      deviceId: scope === 'computer' ? (lockedDeviceId || next.deviceId) : next.deviceId,
      employeeId: scope === 'employee' ? (lockedEmployeeId || next.employeeId) : next.employeeId,
    };
    setValue(scoped);
    const params = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, raw: string) => {
      if (raw) params.set(key, raw);
      else params.delete(key);
    };
    setOrDelete('date', scoped.date);
    setOrDelete('employee_id', scoped.employeeId);
    if (scope === 'global') setOrDelete('device_id', scoped.deviceId);
    else params.delete('device_id');
    setOrDelete('campaign_id', scoped.campaignId);
    setOrDelete('event_type', scoped.eventType);
    setOrDelete('application', scoped.application);
    setOrDelete('domain', scoped.domain);
    setOrDelete('browser', scoped.browser);
    setOrDelete('status', scoped.status);
    setOrDelete('has_snapshot', scoped.hasSnapshot);
    setOrDelete('duration_basis', scoped.evidence);
    setSearchParams(params, { replace: true });
  };

  return [value, update];
}

export default MonitoringFilters;
